import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, Library, FileText, PenTool, TrendingUp, Crown, Flame, Trophy, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProgress();
      logActivity();
    }
  }, [user]);

  const logActivity = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/dashboard/activity`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dashboard/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const isPremium = user.subscription_status === 'premium';

  const stats = [
    { icon: <BookOpen className="w-6 h-6" />, label: 'Grammar Completed', value: progress?.grammar_completed || 0, color: 'bg-blue-50 text-blue-600' },
    { icon: <Library className="w-6 h-6" />, label: 'Vocabulary Learned', value: progress?.vocab_completed || 0, color: 'bg-green-50 text-green-600' },
    { icon: <FileText className="w-6 h-6" />, label: 'Reading Exercises', value: progress?.reading_completed || 0, color: 'bg-blue-50 text-blue-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white flex flex-col">
      <Navbar />

      <main className="flex-1 py-8 px-6">
        <div className="container mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-2" data-testid="dashboard-welcome">
              Welcome back, {user.name}!
            </h1>
            <p className="text-babyblue-600 text-lg">Continue your German learning journey</p>
          </div>

          {/* Gamification Section */}
          {progress?.streak && (
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-semibold text-babyblue-900 mb-4">Your Achievements</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card p-6 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Flame className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-babyblue-600">Current Streak</p>
                      <p className="text-3xl font-bold text-babyblue-900">{progress.streak.current} days</p>
                    </div>
                  </div>
                  <p className="text-xs text-babyblue-500">Longest: {progress.streak.longest} days</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="card p-6 bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <Trophy className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-babyblue-600">Total Points</p>
                      <p className="text-3xl font-bold text-babyblue-900">{progress.streak.points}</p>
                    </div>
                  </div>
                  <p className="text-xs text-babyblue-500">Keep learning to earn more!</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="card p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Target className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-babyblue-600">Achievements</p>
                      <p className="text-3xl font-bold text-babyblue-900">{progress.achievements?.length || 0}</p>
                    </div>
                  </div>
                  <p className="text-xs text-babyblue-500">Badges earned</p>
                </motion.div>
              </div>
            </div>
          )}

          {/* Progress Stats */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-babyblue-900 mb-4">Your Progress</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card p-6"
                  data-testid={`stat-card-${index}`}
                >
                  <div className={`inline-flex p-3 rounded-lg ${stat.color} mb-3`}>
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-babyblue-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-babyblue-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-babyblue-900 mb-4">Continue Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <QuickLinkCard
                icon={<BookOpen className="w-8 h-8 text-primary" />}
                title="Grammar Lessons"
                description="Master German grammar from A1 to C2"
                link="/grammar"
              />
              <QuickLinkCard
                icon={<Library className="w-8 h-8 text-primary" />}
                title="Vocabulary"
                description="Build your German vocabulary"
                link="/vocabulary"
              />
              <QuickLinkCard
                icon={<FileText className="w-8 h-8 text-primary" />}
                title="Reading"
                description="Improve comprehension skills"
                link="/reading"
              />
              <QuickLinkCard
                icon={<TrendingUp className="w-8 h-8 text-primary" />}
                title="Track Progress"
                description="Monitor your improvement"
                link="/dashboard"
              />
            </div>
          </div>

          {/* Ad Revenue Space */}
          <div className="ad-container mt-8" data-testid="ad-placeholder">
            <p>Advertisement Space</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const QuickLinkCard = ({ icon, title, description, link }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(link)}
      data-testid={`quick-link-${title.toLowerCase()}`}
    >
      <div className="mb-4">{icon}</div>
      <h3 className="font-heading text-lg font-semibold text-babyblue-900 mb-2">{title}</h3>
      <p className="text-babyblue-600 text-sm">{description}</p>
    </motion.div>
  );
};

export default Dashboard;
